import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../hooks/useLogin';
import { useShopState } from '../hooks/useShopState';
import { useVerificationWall } from '../hooks/useVerificationWall';
import EmailInput from '../components/login/EmailInput';
import AvailableProviders from '../components/login/AvailableProviders';
import PasskeyLoginEntry from '../components/login/PasskeyLoginEntry';
import AlbumAssoc from '../components/albums/AlbumAssoc';
import EntryCard from '../components/common/EntryCard';
import { FaRightToBracket } from 'react-icons/fa6';
import './Login.scss';
import { TOKEN_KEY, GUEST_TOKEN_KEY } from '../api/queryHooks/user';

interface LoginLocationState {
  from?: string;
  guestCheckout?: boolean;
  registrationRequired?: boolean;
}

const Login = () => {
  const { t } = useTranslation();
  const { isLoggedIn, isGuest } = useLogin();
  // Opening an album mints a guest session, which a shop that is not open
  // refuses; while paused it also refuses album browsing.
  const { closedForMe, pausedForMe } = useShopState();
  const albumEntry = !closedForMe && !pausedForMe;
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const currStep = email ? "authProviders" : "email";

  // Get return path and guest checkout flag from location state
  const state = (location.state ?? null) as LoginLocationState | null;
  // Default landing page after a login that didn't come from a deep link: the
  // albums list, not the marketing home page. A `from` set by ProtectedRoute
  // still wins so deep links survive the login bounce.
  const returnPath = state?.from || "/albums";
  const isGuestCheckout = state?.guestCheckout === true;
  const registrationRequired = state?.registrationRequired === true;

  const { pending: verificationPending, ready: verificationReady } = useVerificationWall();

  useEffect(() => {
    // Only redirect if logged in as a real user (not a guest)
    if (!isLoggedIn || isGuest) return;
    // A customer the shop requires to confirm their email (a fresh signup,
    // typically) goes to the code page first, even when the return path
    // itself would not be walled; the page sends them on to `from`.
    if (!verificationReady) return;
    if (verificationPending) {
      navigate('/verify-email/code', { replace: true, state: { from: returnPath } });
    } else {
      navigate(returnPath);
    }
  }, [isLoggedIn, isGuest, navigate, returnPath, verificationPending, verificationReady]);

  // A guest can reach this page without going through TopNav's login link
  // (Home redirects here, or the album-code form below just minted the
  // guest session). Stash the guest token the same way that link does, so
  // a real login afterwards transfers the guest's albums and cart.
  useEffect(() => {
    if (!isGuest) return;
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      localStorage.setItem(GUEST_TOKEN_KEY, currentToken);
    }
  }, [isGuest]);

  return (
    <>
      {/* One row: sign-in panel left, album-code entry right from the desktop
          breakpoint; stacked (sign-in first) below it. Opening an album mints
          a guest session and the form navigates to the opened album. */}
      <div className="columns is-desktop is-centered login-page">
        {/* login-panel scopes the sign-in widgets for E2E selectors — the
            album-code form also has a text input and a primary submit. */}
        <div className="column is-5-desktop login-panel">
          <EntryCard
            icon={<FaRightToBracket size={24} />}
            title={t('login.title')}
            subtitle={t('login.subtitle')}
            headingLevel="h1"
          >
            {isGuestCheckout && (
              <div className="notification is-info is-light mb-4">
                {t('login.guest_checkout_message')}
              </div>
            )}
            {registrationRequired && (
              <div className="notification is-info is-light mb-4">
                {t('login.registration_required_message')}
              </div>
            )}
            { currStep === "email" && <PasskeyLoginEntry /> }
            { currStep === "email" && <EmailInput onEmailComplete={(email) => setEmail(email)}/> }
            { currStep === "authProviders" && <AvailableProviders email={email} onChangeEmail={() => setEmail("")}/> }
          </EntryCard>
        </div>
        {/* Vertical "or" rule between the widgets; only shown at desktop
            width (CSS), where they sit side by side. Same copy as the
            divider between the sign-in options. */}
        {albumEntry && (
          <>
            <div className="column is-narrow login-or" aria-hidden="true">
              <span className="login-or-label">{t('login.or_divider')}</span>
            </div>
            <div className="column is-5-desktop login-album-entry">
              <AlbumAssoc />
            </div>
          </>
        )}
      </div>
    </>
  )
};

export default Login;
